import { createTheme, alpha } from "@mui/material/styles";

// Brand tokens shared between light and dark modes.
export const brand = {
  primary: "#4f7cff",
  primaryDeep: "#2546e0",
  secondary: "#22d3ee",
  accent: "#a855f7",
  success: "#22c55e",
};

// Accent gradients reused across hero, buttons and section backgrounds.
export const gradients = {
  brand: "linear-gradient(135deg, #4f7cff 0%, #22d3ee 100%)",
  brandSoft: "linear-gradient(135deg, #6d8bff 0%, #57e0f0 100%)",
  violet: "linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)",
  aurora:
    "radial-gradient(circle at 20% 20%, rgba(79,124,255,0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(168,85,247,0.30), transparent 45%), radial-gradient(circle at 60% 80%, rgba(34,211,238,0.30), transparent 50%)",
};

const fontStack = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "Roboto",
  "Helvetica Neue",
  "Arial",
  "sans-serif",
].join(",");

export function getDesignTokens(mode) {
  const isDark = mode === "dark";

  return {
    palette: {
      mode,
      primary: { main: brand.primary, dark: brand.primaryDeep, contrastText: "#ffffff" },
      secondary: { main: brand.secondary, contrastText: "#04121a" },
      success: { main: brand.success },
      ...(isDark
        ? {
            background: { default: "#070b18", paper: "#0e1428" },
            text: { primary: "#eef2ff", secondary: alpha("#c7d2fe", 0.7) },
            divider: alpha("#94a3b8", 0.16),
          }
        : {
            background: { default: "#f6f8ff", paper: "#ffffff" },
            text: { primary: "#0b1220", secondary: alpha("#0b1220", 0.62) },
            divider: alpha("#0b1220", 0.1),
          }),
    },
    shape: { borderRadius: 16 },
    typography: {
      fontFamily: fontStack,
      h1: { fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.05 },
      h2: { fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.1 },
      h3: { fontWeight: 700, letterSpacing: "-0.02em" },
      h4: { fontWeight: 700, letterSpacing: "-0.015em" },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 500 },
      button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
      overline: { fontWeight: 700, letterSpacing: "0.18em" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "html, body, #root": { height: "100%" },
          body: {
            scrollBehavior: "smooth",
            backgroundColor: isDark ? "#070b18" : "#f6f8ff",
          },
          "::selection": {
            background: alpha(brand.primary, 0.3),
          },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            background: alpha(brand.primary, isDark ? 0.4 : 0.25),
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 12, paddingInline: 22, paddingBlock: 10 },
          containedPrimary: {
            background: gradients.brand,
            boxShadow: `0 10px 30px -10px ${alpha(brand.primary, 0.7)}`,
            "&:hover": { background: gradients.brandSoft },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${isDark ? alpha("#94a3b8", 0.14) : alpha("#0b1220", 0.07)}`,
            backgroundImage: "none",
            backgroundColor: isDark ? alpha("#0e1428", 0.7) : "#ffffff",
            backdropFilter: "blur(8px)",
            transition: "transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s ease, border-color .35s ease",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
        },
      },
      MuiAppBar: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
    },
  };
}

export function buildTheme(mode) {
  return createTheme(getDesignTokens(mode));
}
